import { audio } from '../systems/audio/AudioSystem.js';
import { RepriseClient } from '../systems/net/RepriseClient.js';

/**
 * Choir hub (REPRISE.md §6, R4): join or found a ~20-player group, see the
 * choir standings ranked by member Resonance, and leave. A Choir is where a
 * contribution shows up somewhere it's actually seen rather than vanishing
 * into the global bar. Read-only for guests; acting needs sign-in.
 */
export class RepriseChoirMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.busy = false;
    this._flash = null;
    this.myChoir = null;
    this.standings = [];
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay reprise-overlay';
    this._renderShell();
    this.root.appendChild(this.el);
    this._load();
  }

  get signedIn() {
    return Boolean(this.save.accountUsername);
  }

  _renderShell() {
    const kicker = document.createElement('div');
    kicker.className = 'menu-kicker';
    kicker.textContent = 'REPRISE';
    this.el.appendChild(kicker);

    this.heading = document.createElement('div');
    this.heading.className = 'menu-title';
    this.heading.textContent = 'CHOIRS';
    this.el.appendChild(this.heading);

    this.subtitle = document.createElement('div');
    this.subtitle.className = 'menu-subtitle';
    this.subtitle.textContent = 'Rebuild together — a choir is where your part is seen.';
    this.el.appendChild(this.subtitle);

    this.body = document.createElement('div');
    this.body.className = 'reprise-body';
    this.el.appendChild(this.body);

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  async _load() {
    const [mine, standings] = await Promise.all([
      this.signedIn ? RepriseClient.getMyChoir() : Promise.resolve({ ok: true, choir: null }),
      RepriseClient.getChoirStandings(),
    ]);
    if (!this.el.isConnected) return;
    this.myChoir = mine.ok ? mine.choir : null;
    this.standings = standings.ok ? standings.standings : [];
    this._render();
  }

  _render() {
    this.subtitle.textContent = this._flash || 'Rebuild together — a choir is where your part is seen.';
    this._flash = null;
    this.body.innerHTML = '';

    if (!this.signedIn) {
      const gate = document.createElement('div');
      gate.className = 'reprise-empty';
      gate.textContent = 'Sign in to join or found a choir.';
      this.body.appendChild(gate);
      this.body.appendChild(this._renderStandings());
      return;
    }

    this.body.appendChild(this.myChoir ? this._renderMyChoir() : this._renderCreate());
    this.body.appendChild(this._renderStandings());
  }

  _renderMyChoir() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = 'YOUR CHOIR';
    panel.appendChild(title);

    const name = document.createElement('div');
    name.className = 'reprise-front-name';
    name.textContent = this.myChoir.name;
    panel.appendChild(name);

    const leave = document.createElement('button');
    leave.type = 'button';
    leave.className = 'menu-button';
    leave.textContent = 'Leave Choir';
    leave.disabled = this.busy;
    leave.addEventListener('click', () => this._leave());
    panel.appendChild(leave);
    return panel;
  }

  _renderCreate() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = 'FOUND A CHOIR';
    panel.appendChild(title);

    const form = document.createElement('div');
    form.className = 'reprise-trade-form';
    form.innerHTML = `<label>Name<input type="text" data-choir-name maxlength="24" placeholder="Choir name"></label>`;
    panel.appendChild(form);
    const input = form.querySelector('[data-choir-name]');

    const create = document.createElement('button');
    create.type = 'button';
    create.className = 'menu-button menu-button-primary';
    create.textContent = 'Create';
    create.disabled = this.busy;
    create.addEventListener('click', () => this._create(input.value));
    panel.appendChild(create);

    const hint = document.createElement('div');
    hint.className = 'reprise-front-hint';
    hint.textContent = 'Or join one from the standings below.';
    panel.appendChild(hint);
    return panel;
  }

  _renderStandings() {
    const panel = document.createElement('div');
    panel.className = 'reprise-salvage';
    const title = document.createElement('div');
    title.className = 'reprise-salvage-title';
    title.textContent = `STANDINGS (${this.standings.length})`;
    panel.appendChild(title);

    if (this.standings.length === 0) {
      const hint = document.createElement('div');
      hint.className = 'reprise-front-hint';
      hint.textContent = 'No choirs yet — be the first to found one.';
      panel.appendChild(hint);
      return panel;
    }

    const canJoin = this.signedIn && !this.myChoir;
    this.standings.forEach((c, i) => {
      const row = document.createElement('div');
      row.className = 'reprise-trade-row';
      if (this.myChoir && c.choir_id === this.myChoir.id) row.classList.add('reprise-choir-mine');
      const rank = document.createElement('span');
      rank.className = 'reprise-choir-rank';
      rank.textContent = `#${i + 1}`;
      const name = document.createElement('span');
      name.className = 'reprise-choir-name';
      name.textContent = c.name; // textContent — other users' data
      const stat = document.createElement('span');
      stat.className = 'reprise-choir-stat';
      stat.textContent = `${Number(c.total_resonance).toLocaleString()} Res · ${c.members}/30`;
      const meta = document.createElement('div');
      meta.className = 'reprise-trade-sides';
      meta.append(rank, name, stat);
      row.appendChild(meta);

      if (canJoin && c.members < 30) {
        const join = document.createElement('button');
        join.type = 'button';
        join.className = 'menu-button reprise-trade-act menu-button-primary';
        join.textContent = 'Join';
        join.disabled = this.busy;
        join.addEventListener('click', () => this._join(c.choir_id));
        row.appendChild(join);
      }
      panel.appendChild(row);
    });
    return panel;
  }

  async _create(name) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.createChoir((name || '').trim());
    if (!this.el.isConnected) return;
    this.busy = false;
    this._flash = result.ok ? 'Choir founded.' : (result.error || 'Could not create that choir.');
    await this._load();
  }

  async _join(choirId) {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.joinChoir(choirId);
    if (!this.el.isConnected) return;
    this.busy = false;
    this._flash = result.ok ? 'Joined the choir.' : (result.error || 'Could not join.');
    await this._load();
  }

  async _leave() {
    if (this.busy) return;
    this.busy = true;
    audio.playSfx('click');
    const result = await RepriseClient.leaveChoir();
    if (!this.el.isConnected) return;
    this.busy = false;
    this._flash = result.ok ? 'Left the choir.' : 'Could not leave.';
    await this._load();
  }

  destroy() {
    this.el.remove();
  }
}
